<script setup>
/**
 * RM-302 — the message RM wrote for the member, ready for them to send.
 *
 * RM never sends. Tapping Text or Email opens the member's own Messages or
 * Mail app with the person already addressed and the words already there, and
 * they decide whether to press send.
 *
 * The number and the email address are never in the chat message. The reply
 * carries an opaque draft id; this component fetches the destination over the
 * member's own session, which is where the reveal is authorised and logged.
 * So the digits are not in the stored conversation, the Slack mirror, or
 * anything an admin can export.
 */
import { computed, onMounted, ref } from 'vue'
import axios from '@axios'

const props = defineProps({
  draftId: { type: String, required: true },
})

const draft = ref(null)
const loading = ref(true)
const error = ref('')


onMounted(async () => {
  try {
    const { data } = await axios.get(`/profile/send-drafts/${props.draftId}`)
    draft.value = data
  } catch (e) {
    error.value = e?.response?.data?.message || 'This message could not be opened.'
  } finally {
    loading.value = false
  }
})
</script>

<template>
  <div class="agent-send-contact">
    <div
      v-if="loading"
      class="agent-send-contact__loading"
    >
      <VProgressCircular
        indeterminate
        size="18"
        width="2"
      />
    </div>

    <p
      v-else-if="error"
      class="agent-send-contact__error"
    >
      {{ error }}
    </p>

    <template v-else-if="draft">
      <!--
        Just the message (Kapil, 2026-09-22). The contact line and the subject
        sat above it as though they were part of what he was sending; the reply
        above already says who it is for, and Mail shows the subject.
      -->
      <p class="agent-send-contact__body">
        {{ draft.body }}
      </p>
      <div class="agent-send-contact__actions">
        <!--
          A plain link, so the phone hands it to Messages or Mail itself. The
          href comes from the server already built and encoded.
        -->
        <a
          v-for="a in draft.actions"
          :key="a.kind"
          :href="a.href"
          class="agent-send-contact__btn"
        >
          <VIcon
            size="16"
            :icon="a.kind === 'text' ? 'mdi-message-text-outline' : 'mdi-email-outline'"
          />
          {{ a.label }}
        </a>
      </div>
      <p class="agent-send-contact__note">
        You send this yourself — RM doesn't send it for you.
      </p>
    </template>
  </div>
</template>

<style scoped>
.agent-send-contact {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  border-radius: 10px;
  margin-block-start: 8px;
  padding: 12px;
}



.agent-send-contact__body {
  font-size: 14px;
  margin: 0 0 10px;
  white-space: pre-wrap;
}

.agent-send-contact__actions {
  display: flex;
  gap: 8px;
}

.agent-send-contact__btn {
  align-items: center;
  background: rgb(var(--v-theme-primary));
  border-radius: 8px;
  color: rgb(var(--v-theme-on-primary));
  display: inline-flex;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  padding: 8px 14px;
  text-decoration: none;
}

.agent-send-contact__note {
  font-size: 12px;
  margin: 8px 0 0;
  opacity: 0.7;
}

.agent-send-contact__error {
  font-size: 13px;
  margin: 0;
  opacity: 0.8;
}

.agent-send-contact__loading {
  display: flex;
  justify-content: center;
  padding: 12px;
}
</style>
